/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks\useCustomTaskQuickList.ts
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the maintenance module.
────────────────────────────────────────────────────────────────*/


import { useState, useCallback, useEffect, useRef } from 'react';
import { fetchCustomTasks, CustomTask } from '../api';

interface Props { isOpen: boolean; }

export function useCustomTaskQuickList({ isOpen }: Props) {
    const [tasks, setTasks] = useState<CustomTask[]>([]);
    const [search, setSearch] = useState('');
    const [isLoadingInitial, setIsLoadingInitial] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const loadingRef = useRef(false);

    const loadInitial = useCallback(async () => {
        if (loadingRef.current) return;
        loadingRef.current = true;
        setIsLoadingInitial(true);
        try {
            const result = await fetchCustomTasks({ page: 1, limit: 100 });
            setTasks(result.data);
        } catch { setTasks([]); }
        finally { setIsLoadingInitial(false); loadingRef.current = false; }
    }, []);

    const loadSearch = useCallback(async (q: string) => {
        const s = q.trim();
        if (!s || s.length < 2) return;
        setIsSearching(true);
        try {
            const result = await fetchCustomTasks({ page: 1, limit: 50, search: s });
            setTasks(result.data);
        } catch { setTasks([]); }
        finally { setIsSearching(false); }
    }, []);

    // Load on open
    useEffect(() => {
        if (isOpen && tasks.length === 0 && !loadingRef.current) void loadInitial();
    }, [isOpen, tasks.length, loadInitial]);

    // Debounced search / reset on close
    useEffect(() => {
        if (!isOpen) { setSearch(''); return; }
        const s = search.trim();
        if (!s || s.length < 2) { void loadInitial(); return; }
        const t = setTimeout(() => void loadSearch(search), 800);
        return () => clearTimeout(t);
    }, [search, isOpen, loadSearch, loadInitial]);

    return { tasks, search, setSearch, isLoadingInitial, isSearching };
}
